import React, { useMemo } from 'react';
import { ClockCircleOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { Typography } from 'antd';
import PropTypes from 'prop-types';
import { DateTime } from 'luxon';

import Colors from '../constants/Colors';
import BasicTimer from '../common/clock/BasicTimer';
import { getIdMap } from '../helpers/helpers';

const { Text } = Typography;

export default function EquipmentListClock({
  id,
  name,
  currentProjectId,
  currentLocationText,
  onTimerClick,
  lastTask = {},
}) {
  const projects = useSelector((state) => state.projects.projects);
  const users = useSelector((state) => state.users.users);

  const projectMap = useMemo(() => getIdMap(projects), [projects]);
  const userMap = useMemo(() => getIdMap(users), [users]);

  const handleClockClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    onTimerClick({
      id, name, currentProjectId, currentLocationText,
    });
  };

  const formattedDay = useMemo(() => {
    if (!lastTask) return {};
    const { startTime, endTime } = lastTask;
    if (!endTime) {
      return {
        currentTask: {
          ...lastTask,
          startTime,
          type: 'work',
        },
      };
    }
    // BasicTimer shows current time for closed tasks, relative to NOW.
    const diff = endTime - startTime;
    return {
      currentTask: {
        ...lastTask,
        startTime: DateTime.local().toMillis() - diff,
        type: 'work',
      },
    };
  }, [lastTask]);

  const isRunning = useMemo(() => {
    if (!lastTask) return false;
    const { startTime, endTime } = lastTask;
    return startTime && !endTime;
  }, [lastTask]);

  const {
    project,
    location,
    user,
  } = useMemo(() => {
    if (!lastTask) return {};
    const { projectId, location: lastLocation, userId } = lastTask;
    return {
      project: projectMap[projectId]?.name,
      location: lastLocation,
      user: userMap[userId]?.name,
    };
  }, [lastTask, projectMap, userMap]);

  const backgroundColor = isRunning ? Colors.GREEN_50 : Colors.GRAY_100;
  const clockColor = isRunning ? Colors.GREEN_700 : Colors.GRAY_800;

  return (
    <div className="user-clock-display" onClick={handleClockClick}>
      <div className="user-runtime" style={{ backgroundColor }}>
        <ClockCircleOutlined style={{ color: clockColor, marginBottom: 2, marginRight: 4 }} />
        <BasicTimer
          includeDays
          formattedDay={formattedDay}
          style={{ fontSize: 12, fontFamily: 'open-sans-600', color: clockColor }}
        />
      </div>
      { (project || location) && (
        <div className="project-pill">
          <Text style={{ color: Colors.GRAY_800 }}>{project || location}</Text>
        </div>
      )}
      { user && (
        <div className="costcode-pill">
          <Text style={{ color: Colors.GRAY_800 }}>{user}</Text>
        </div>
      )}
    </div>
  );
}

EquipmentListClock.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  currentProjectId: PropTypes.string.isRequired,
  currentLocationText: PropTypes.string.isRequired,
  onTimerClick: PropTypes.func.isRequired,
  lastTask: PropTypes.shape({}),
};

EquipmentListClock.defaultProps = {
  lastTask: {},
};
